import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  HomeWork as AddressIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import assessmentService from '../../services/assessmentService';

const AssessmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Fetch assessment data
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const data = await assessmentService.getAssessment(id);
        setAssessment(data);
      } catch (err) {
        console.error(`Error fetching assessment ${id}:`, err);
        setError('Failed to load assessment details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssessment();
  }, [id]);
  
  // Handle send to CRM
  const handleSendToCrm = async () => {
    try {
      setSending(true);
      await assessmentService.sendToCrm(id);
      // Show success message
    } catch (err) {
      console.error('Error sending assessment to CRM:', err);
      // Handle error
    } finally {
      setSending(false);
    }
  };
  
  // Get score color
  const getScoreColor = (score) => {
    if (score > 100) return 'error';
    if (score > 50) return 'warning';
    return 'success';
  };
  
  // Get urgency color
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high':
        return 'error';
      case 'medium-high':
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'info';
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assessments')}
          sx={{ mb: 3 }}
        >
          Back to Assessments
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  if (!assessment) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assessments')}
          sx={{ mb: 3 }}
        >
          Back to Assessments
        </Button>
        <Alert severity="warning">Assessment not found</Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/assessments')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" fontWeight="300">
            Assessment Details
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSendToCrm}
          disabled={sending}
        >
          {sending ? 'Sending...' : 'Send to CRM'}
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* Left column - Assessment summary */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Assessment Summary
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {format(parseISO(assessment.createdAt), 'MMMM dd, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source
                  </Typography>
                  <Chip
                    label={assessment.source.replace('_', ' ')}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary">
                Result
              </Typography>
              <Box display="flex" alignItems="center" sx={{ mt: 1, mb: 2 }}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    mr: 2,
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={Math.min((assessment.result.score / 150) * 100, 100)}
                    color={getScoreColor(assessment.result.score)}
                    size={60}
                    thickness={5}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2" component="div" color="text.secondary">
                      {assessment.result.score}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {assessment.result.category}
                  </Typography>
                  <Chip
                    label={assessment.result.urgency.replace('-', ' ')}
                    color={getUrgencyColor(assessment.result.urgency)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary">
                Recommendation
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mt: 1,
                  bgcolor: 'background.default',
                  borderLeftWidth: 4,
                  borderLeftColor: getUrgencyColor(assessment.result.urgency) + '.main',
                }}
              >
                <Typography variant="body2">
                  {assessment.result.recommendation}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
          
          {/* Customer information */}
          {assessment.customer ? (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Customer Information
                </Typography>
                
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center">
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      {assessment.customer.name}
                    </Typography>
                  </Box>
                  
                  {assessment.customer.email && (
                    <Box display="flex" alignItems="center">
                      <EmailIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        {assessment.customer.email}
                      </Typography>
                    </Box>
                  )}
                  
                  {assessment.customer.phone && (
                    <Box display="flex" alignItems="center">
                      <PhoneIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        {assessment.customer.phone}
                      </Typography>
                    </Box>
                  )}
                  
                  {assessment.customer.address && (
                    <Box display="flex" alignItems="center">
                      <AddressIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        {assessment.customer.address}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => navigate(`/customers/${assessment.customer._id}`)}
                >
                  View Customer Profile
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">
                    Anonymous Assessment
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  This assessment was submitted anonymously and is not linked to a customer profile.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
        
        {/* Right column - Assessment answers */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Assessment Answers
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Question</TableCell>
                      <TableCell>Answer</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell align="right">Info</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(assessment.answers).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell>
                          {key.replace(/([A-Z])/g, ' $1')
                            .replace(/_/g, ' ')
                            .replace(/^\w/, c => c.toUpperCase())}
                        </TableCell>
                        <TableCell>
                          {typeof value === 'object' ? value.value : value}
                        </TableCell>
                        <TableCell align="right">
                          {typeof value === 'object' && value.score !== undefined ? (
                            <Chip
                              label={value.score}
                              color={value.score > 20 ? 'error' : value.score > 10 ? 'warning' : 'success'}
                              size="small"
                            />
                          ) : null}
                        </TableCell>
                        <TableCell align="right">
                          {typeof value === 'object' && value.notes ? (
                            <Tooltip title={value.notes}>
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Score Breakdown
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((assessment.result.score / 150) * 100, 100)}
                  color={getScoreColor(assessment.result.score)}
                  sx={{ height: 10, borderRadius: 5, mb: 1 }}
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    0 - Minor Issues
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    150+ - Critical
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssessmentDetail;